import { memo, useState, useContext, useEffect, FC } from "react";
import api from "api/landlord/applications";
import { DynamicKeyValue } from "../components/SmartRender/DynamicKeyValue";
import { findKeyBySuffix } from "../utils/findKeyBySuffix";
import { Applicant } from "types/application";
import { ReportHeader } from "../components/ReportHeader";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { dateInfo, normalizeDate } from "../utils/renderer";
import { Section } from "../components/Section";
import { sortAndFilterObjectValues } from "../components/SmartRender/ObjectItem";
import { Stat } from "../components/Stat";
import { BarChart02 } from "untitledui-js-base";
import { getVerificationQueryKeys } from "../utils/getVerificationQueryKeys";
import { VerificationNames, SurveyStatus } from "shared/types";
import {
  PrintAllContext,
  PrintReady
} from "views/applications-id-report/providers/PrintProvider";
import {
  HousingHistoryReportResult,
  ReportAddress,
  VerificationReport
} from "types/landlord/reports";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { Show } from "shared/components";

interface RentalHistorySliceProps {
  applicant: Applicant;
  verificationId: number;
  verificationSchemas?: {
    [Key in VerificationNames]: VerificationSchema;
  };
}

const groupLandlordPrefix = (value: object) => {
  // place all keys with landlord_ prefix at landlord key
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return Object.entries(value || {}).reduce(
    (acc, [k, v]: [string, unknown]) => {
      if (k === "address" && typeof v === "string") {
        const splitedAddress = v.split(",");

        ["US", "USA"].includes(
          splitedAddress.at(-1)?.trim().toUpperCase() || ""
        ) && splitedAddress.pop();

        acc.full_address = v;

        if (!acc._address) acc._address = {};

        if (splitedAddress.length === 3) {
          const [street_address, city, state] = splitedAddress;
          if (street_address)
            acc._address.street_address = street_address.trim();
          if (city) acc._address.city = city.trim();
          if (state) {
            acc._address.state = state.trim().replaceAll(/\d/g, "");

            const zipCode = state.trim().replace(/\D/g, "");

            acc._address.zip_code = zipCode;
          }
        } else if (splitedAddress.length === 4) {
          const [street_address, street_address2, city, rest] = splitedAddress;

          if (street_address)
            acc._address.street_address = street_address.trim();
          if (street_address2)
            acc._address.street_address2 = street_address2.trim();
          if (city) acc._address.city = city.trim();

          if (rest) {
            let state = "";
            let zip_code = "";

            const splitedRest = rest.trim().split(" ");

            if (splitedRest.length === 2) {
              state = splitedRest[0];
              zip_code = splitedRest[1];
            } else if (splitedRest.length === 3) {
              state = splitedRest[0] + " " + splitedRest[1];
              zip_code = splitedRest[2];
            }

            if (state) acc._address.state = state.trim();
            if (zip_code) acc._address.zip_code = zip_code.trim();
          }
        }
      } else if (k.startsWith("landlord_"))
        acc.landlord = {
          ...(typeof acc.landlord === "object" ? acc.landlord : {}),
          [k.replace("landlord_", "")]: v
        };
      else acc[k] = v as string;

      return acc;
    },
    {} as {
      full_address?: string;
      address?: string;
      move_in_date?: string;
      move_out_date?: string;
      current_residence?: boolean;
      lease_document?: any[];
      monthly_mortgage_payment?: string;
      monthly_payment?: string;
      housing_status?: string;
      reason_for_leaving?: string;
      landlord?: Record<string, unknown>;
      _address?: {
        street_address?: string;
        street_address2?: string;
        city?: string;
        state?: string;
        zip_code?: string;
      };
    }
  );
};

const sortLandlordSurveyKeys = (surveyData: {
  [key: string]: string;
}): { [key: string]: string } => {
  const surveyOrder = [
    "Is the Tenant currently renting from you?",
    "Please confirm move-in date",
    "Please confirm move-out date",
    "Please confirm lease expiration date.",
    "Did the Tenant give proper notice of intent to vacate?",
    "Did the Tenant have late payments during the last 12 months of their lease?",
    "Has the Tenant had a late payment within the last 12 months?",
    "How many late payments were made during the last 12 months of their lease?",
    "Did the Tenant have payments returned for non-sufficient funds during the last 12 months of their lease?",
    "How many payments were returned for non-sufficient sufficient during the last 12 months of their lease?",
    "Did the Tenant have a rent payment returned due to non-sufficient funds within the last 12 months?",
    "How many times was a payment returned for non-sufficient funds in the last 12 months?",
    "Would you rent to this Tenant again?"
  ];

  const trimmedSurveyData = Object.entries(surveyData).reduce(
    (acc, [key, value]) => {
      acc[key.trim()] = value;
      return acc;
    },
    {}
  );

  return surveyOrder.reduce((sorted, key) => {
    const trimmedKey = key.trim();
    if (trimmedKey in trimmedSurveyData)
      sorted[trimmedKey] = trimmedSurveyData[trimmedKey];
    return sorted;
  }, {});
};

const HousingHistoryReport: FC<RentalHistorySliceProps> = ({
  applicant,
  verificationId,
  verificationSchemas
}) => {
  const [landlordSurveysData, setLandlordSurveysData] = useState({});
  const { documentPrintingState, setDocumentPrintingState } =
    useContext(PrintAllContext);
  const { data, isLoading } = useQuery<
    VerificationReport<HousingHistoryReportResult>
  >({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.HousingHistory,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then((d) => {
          setLandlordSurveysData(d.verification.landlord_surveys_data);
          return d.verification;
        })
  });

  useEffect(() => {
    const printingState = documentPrintingState?.find(
      (x) => x.reportId == verificationId
    );
    if (printingState?.status !== "ready" && data) {
      const numDocs =
        data.report.address.reduce((acc, item) => {
          const leaseDoc = item.rent?.lease_document;

          const leaseDocItems = leaseDoc?.filter(
            (file) => file.asset?.extension === "pdf"
          );
          if (leaseDocItems?.length)
            acc +=
              leaseDocItems.filter((file) => {
                return file.asset?.extension === "pdf";
              }).length || 0;

          return acc;
        }, 0) || 0;

      setDocumentPrintingState((prev) => {
        if (!data.id) return prev;
        if (!prev)
          return [
            {
              status: "ready",
              reportId: data.id,
              numDocuments: numDocs
            }
          ];

        const newState = [...prev];

        const foundIndex = newState.findIndex((x) => x.reportId == data.id);

        if (foundIndex >= 0)
          newState[foundIndex] = {
            status: "ready",
            reportId: data.id,
            numDocuments: numDocs
          };

        return newState;
      });
    }
  }, [documentPrintingState, data]);

  if (isLoading)
    return (
      <>
        <ReportHeader
          title="Housing history"
          reportDate=""
          verificationId={0}
        />
        <Loader />
      </>
    );

  const itemsSortedByMoveInDateDesc = data?.report.address.sort((a, b) => {
    const reportA = Object.values(a).find(
      (v) => typeof v !== "string"
    ) as ReportAddress["other"];
    const reportB = Object.values(b).find(
      (v) => typeof v !== "string"
    ) as ReportAddress["other"];

    if (reportA?.current_residence && !reportB?.current_residence) return -1;
    if (reportB?.current_residence && !reportA?.current_residence) return 1;

    if (!reportA) return 1;
    if (!reportB) return -1;

    const aDate = reportA.move_in_date || reportA.start_date;
    const bDate = reportB.move_in_date || reportB.start_date;
    if (!aDate) return 1;
    if (!bDate) return -1;

    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  return (
    <>
      <ReportHeader
        title="Housing history"
        secondaryText="(self-reported)"
        reportDate={data?.updated_at}
        verificationId={verificationId}
        idsWhichMayHaveFiles={[verificationId]}
      />

      <PrintReady verificationId={verificationId} />

      <div className="space-y-4">
        <Section
          sectionTitle="Housing history summary"
          icon={<BarChart02 size="20px" className="stroke-content-500" />}
          maxWidth="none"
        >
          <div className="flex flex-wrap items-center  gap-4">
            <Stat
              width="240px"
              title="Number of residences"
              value={String(data?.report.total_number_of_residences)}
            />
            <Stat
              width="240px"
              title="Total months of residence"
              value={String(data?.report.total_months_of_residence)}
            />
            <Stat
              width="240px"
              title="Average monthly payments"
              value={`$${Number(data?.report.average_monthly_payment_amount).toFixed(0)}`}
            />
            <Show if={verificationSchemas?.rental_verification}>
              <Stat
                width="240px"
                title="Landlord verifications"
                value={`${data?.report.completed_surveys_count ?? 0}/${data?.report.total_surveys_count ?? 0}`}
                subtitle="Surveys completed"
              />
            </Show>
          </div>
        </Section>
        {itemsSortedByMoveInDateDesc?.map(
          (item: ReportAddress, index: number) => {
            const _report = Object.values(item).find(
              (v) => typeof v !== "string"
            );

            if (!_report) return null;

            const report = groupLandlordPrefix(
              typeof _report !== "object" ? {} : _report
            );

            return (
              <Section
                key={index}
                sectionTitle={
                  <div className="flex w-full items-center justify-between gap-2 text-sm font-medium text-content-1000">
                    <span className="flex items-center gap-2">
                      <p className="text-content-1000 text-sm font-bold">
                        {item.housing_type}
                      </p>
                      <div className="w-1.5 h-1.5 bg-alpha-100 rounded-full" />
                      <p className="text-sm text-content-300">
                        {report.full_address}
                      </p>
                    </span>

                    <p className="text-content-300 text-xs">
                      {findKeyBySuffix(report, "start_date")
                        ? dateInfo(item)
                        : report.move_in_date
                          ? `${normalizeDate(report.move_in_date)} - ${report.current_residence ? "Present" : normalizeDate(report.move_out_date)}`
                          : ""}
                    </p>
                  </div>
                }
              >
                <div className="w-full max-w-[600px]">
                  {sortAndFilterObjectValues(Object.entries(report), {
                    order: [
                      "lease_document",
                      "monthly_mortgage_payment",
                      "monthly_payment",
                      "housing_status",
                      "reason_for_leaving",
                      "landlord",
                      "_address"
                    ],
                    filter: [
                      "full_address",
                      "address",
                      "move_in_date",
                      "move_out_date",
                      "current_residence"
                    ]
                  }).map(([key, value]) => (
                    <div key={key}>
                      <DynamicKeyValue detailsKey={key} value={value} />
                      {key === "_address" &&
                      report.landlord?.email &&
                      verificationSchemas?.rental_verification ? (
                        <DynamicKeyValue
                          status={
                            landlordSurveysData[String(report.landlord.email)]
                              ? SurveyStatus.Completed
                              : SurveyStatus.WaitingForLandlord
                          }
                          key="rental_verification_survey"
                          fullDate={true}
                          detailsKey="Rental verification survey"
                          value={
                            landlordSurveysData[String(report.landlord.email)]
                              ? sortLandlordSurveyKeys(
                                  landlordSurveysData[
                                    String(report.landlord.email)
                                  ]
                                )
                              : Object.fromEntries(
                                  [
                                    "Is the Tenant currently renting from you?",
                                    "Please confirm move-in date",
                                    "Please confirm lease expiration date.",
                                    "Is the Tenant current with ALL rental payments?",
                                    "Has the Tenant had a late payment within the last 12 months?",
                                    "How many times within the last 12 months has the Tenant paid late?",
                                    "Did the Tenant have a rent payment returned due to non-sufficient funds within the last 12 months?",
                                    "How many times was a payment returned for non-sufficient funds in the last 12 months?",
                                    "Please confirm move-out date",
                                    "Did the Tenant give proper notice of intent to vacate?",
                                    "Did the Tenant have late payments during the last 12 months of their lease?",
                                    "How many late payments were made during the last 12 months of their lease?",
                                    "Did the Tenant have payments returned for non-sufficient funds during the last 12 months of their lease?",
                                    "How many payments were returned for non-sufficient sufficient during the last 12 months of their lease?",
                                    "Would you rent to this Tenant again?"
                                  ].map((key) => [key, "N/A"])
                                )
                          }
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </Section>
            );
          }
        )}
      </div>
    </>
  );
};

export const HousingHistoryReportSlice = memo(HousingHistoryReport);
